import { useState } from "react";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { IDS } from "@/lib/testIds";
import { createDonationCheckout } from "@/lib/api";
import { DONATION_QUICK_AMOUNTS } from "@/lib/constants";

export default function DonationSection() {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleDonate = async () => {
    setError("");
    const parsed = Number(amount);
    if (!parsed || Number.isNaN(parsed) || parsed <= 0) {
      setError("Please enter a valid amount greater than 0.");
      return;
    }
    if (parsed < 0.5) {
      setError("Minimum donation amount is $0.50.");
      return;
    }
    try {
      setLoading(true);
      const { url } = await createDonationCheckout(parsed);
      if (!url) throw new Error("No checkout URL received");
      window.location.href = url;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Something went wrong";
      setError(msg);
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <section
      id="donate"
      data-testid={IDS.donation.root}
      className="relative py-16 sm:py-24"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="relative bg-gradient-to-br from-rose-50 via-white to-amber-50 rounded-[2.5rem] p-8 sm:p-14 border-2 border-white shadow-[0_20px_60px_rgb(0,0,0,0.06)] overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-rose-200/50 rounded-full"></div>
          <div className="absolute -bottom-20 -left-16 w-64 h-64 bg-amber-200/50 rounded-full"></div>

          <div className="relative text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-rose-400 shadow-lg mb-5">
              <Heart className="w-8 h-8 text-white" fill="currentColor" />
            </div>
            <div className="text-xs font-bold uppercase tracking-widest text-rose-500 mb-2">
              Support PhonX
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate-900 tracking-tight">
              Fuel free literacy — <br />
              <span className="text-rose-500">any amount helps.</span>
            </h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">
              PhonX is free for every learner. Your donation keeps the sounds
              playing, the pictures loading, and the games running for kids and
              adults everywhere.
            </p>

            <div className="mt-8 max-w-md mx-auto">
              <label
                htmlFor={IDS.donation.input}
                className="block text-sm font-bold uppercase tracking-widest text-slate-500 mb-3"
              >
                Enter any amount (USD)
              </label>
              <div className="relative">
                <span className="absolute left-6 top-1/2 -translate-y-1/2 text-3xl font-bold text-slate-400">
                  $
                </span>
                <input
                  id={IDS.donation.input}
                  data-testid={IDS.donation.input}
                  type="number"
                  min="0.50"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full text-center text-3xl font-bold bg-white border-2 border-slate-200 rounded-2xl py-4 pl-14 pr-6 focus:ring-4 focus:ring-rose-100 focus:border-rose-400 outline-none transition-all text-slate-800 placeholder:text-slate-300"
                />
              </div>

              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {DONATION_QUICK_AMOUNTS.map((v) => (
                  <button
                    key={v}
                    data-testid={`donation-quick-${v}`}
                    type="button"
                    onClick={() => setAmount(String(v))}
                    className="px-4 py-2 rounded-full text-sm font-bold bg-white border-2 border-slate-200 text-slate-700 hover:border-rose-300 hover:text-rose-600 transition-colors"
                  >
                    ${v}
                  </button>
                ))}
              </div>

              {error && (
                <p
                  data-testid={IDS.donation.error}
                  className="mt-4 text-sm font-bold text-rose-600"
                >
                  {error}
                </p>
              )}

              <button
                data-testid={IDS.donation.button}
                onClick={handleDonate}
                disabled={loading}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-rose-500 hover:bg-rose-600 disabled:bg-rose-300 text-white font-bold text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 duration-200"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Redirecting…
                  </>
                ) : (
                  <>
                    <Heart className="w-5 h-5" fill="currentColor" /> Donate with Stripe
                  </>
                )}
              </button>
              <p className="mt-3 text-xs text-slate-500">
                Secure payment via Stripe. 100% of your donation funds PhonX.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
